import React, { useState, useEffect } from 'react';
import { Divider, Drawer } from '@dsg-tech/homefield-react';
import useHomr from 'hooks/useHomr';
import { selectSelectedCategory } from 'store/selectors/progressiveHeaderSelector';
import { resetSelectedCategory } from 'store/slices/progressiveHeader';
import { setDisplayedSearchTerm, setSearchInput, setShowMobileSaytDrawer } from 'store/slices/sayt';
import { setHeaderFeaturedTaxo } from 'store/slices/taxonomy';
import {
  breakpointSelector,
  getFeatureFlagByNameSelector,
  headerFeaturedTaxoSelector,
} from 'store/selectors';
import { searchInputSelector } from 'store/selectors/saytSelector';
import { PrimaryCategoryData, HeaderTaxonomy, ColumnCategory, StoreIdentifierName } from 'types';
import { KEY_CODES } from 'utility/constants';
import { ProgressiveNavBackEvent, ProgressiveTaxoViewEvent } from 'utility/analytics-events';
import { useAppDispatch, useAppSelector } from 'store/store';
import ErrorBoundaryWithLogger from 'common/ErrorBoundary/ErrorBoundary';
import UtilityLinks from './UtilityLinks/UtilityLinks';
import StoreLocatorAndSDD from './StoreLocatorAndSDD/StoreLocatorSDD';
import Header from './Header';
import L0Links from './L0Links';
import L1Links from './L1Links';
import L2Links from './L2Links';

interface CustomHeaderRef extends HTMLElement {
  current?: any;
}
interface ProgressiveTaxoProps {
  headerRef: CustomHeaderRef | null;
  showDrawer: boolean;
  setShowDrawer: () => void;
  closing: boolean;
  storeIdentifierName: string;
  navBarData: HeaderTaxonomy | undefined;
}

type TransitionSizes = 'L0' | 'L1' | 'L2';

const ProgressiveHeader = ({
  headerRef,
  showDrawer,
  setShowDrawer,
  closing,
  storeIdentifierName,
  navBarData,
}: ProgressiveTaxoProps) => {
  const [selectedL0, setSelectedL0] = useState<PrimaryCategoryData | null>(null);
  const [selectedL1, setSelectedL1] = useState<ColumnCategory | null>(null);
  const [showL1Links, setShowL1Links] = useState(false);
  const [showL2Links, setShowL2Links] = useState(false);
  const [keyBoardNavigation, setKeyBoardNavigation] = useState(false);
  const [selectedL1Index, setSelectedL1Index] = useState<number>(-1);
  const viewPortBreakpoint = useAppSelector(breakpointSelector);
  const headerFeatured = useAppSelector(headerFeaturedTaxoSelector);
  const modalContainer = document.querySelector(
    '.progressive-drawer-container .hmf-modal-container',
  );
  const PNBackTrackingEnabled = useAppSelector(
    getFeatureFlagByNameSelector('PNBackTrackingEnabled'),
  );

  useHomr(
    'header-featured',
    'default',
    (storeIdentifierName as StoreIdentifierName) || [],
    setHeaderFeaturedTaxo,
  );

  const selectedCategoryName = useAppSelector(selectSelectedCategory);

  const searchInput = useAppSelector(searchInputSelector);

  const dispatch = useAppDispatch();

  const setMaxImportantWidth = (qty: number, setTo100 = false) => {
    modalContainer?.setAttribute(
      'style',
      setTo100 ? 'max-width: 100% !important' : `max-width: ${qty}px !important`,
    );
  };

  const handleTransitions = (size: TransitionSizes) => {
    if (viewPortBreakpoint === 'desktop') {
      if (size === 'L0') {
        setMaxImportantWidth(402);
      } else if (size === 'L1') {
        setMaxImportantWidth(800);
      } else if (size === 'L2') {
        setMaxImportantWidth(1200);
      }
    }
  };

  useEffect(() => {
    if (!showL1Links && !showL2Links && showDrawer) {
      handleTransitions('L0');
    } else if (showL1Links && !showL2Links) {
      handleTransitions('L1');
    } else if (showL1Links && showL2Links) {
      handleTransitions('L2');
    }
  }, [showL1Links, showL2Links, showDrawer, viewPortBreakpoint]);

  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVh();
    window.addEventListener('resize', setVh);

    return () => {
      window.removeEventListener('resize', setVh);
    };
  }, []);

  const handleSearchDisplay = () => {
    const logoElement = document.getElementById('headerLogoProgressiveTaxo');
    const searchIconElement = document.getElementById('searchIconProgressiveTaxo');
    if (logoElement) {
      logoElement.style.display = 'none';
    }
    if (searchIconElement) {
      searchIconElement.style.display = 'none';
    }
  };

  const setDisplaySaytDrawer = (value: boolean) => {
    dispatch(setShowMobileSaytDrawer(value));
  };

  const handleSayt = () => {
    if (searchInput && searchInput.length > 0) {
      dispatch(setSearchInput(''));
      dispatch(setDisplayedSearchTerm(''));
    }
    setShowDrawer();
    setDisplaySaytDrawer(true);
  };

  const navigateBackToL0Menu = () => {
    if (PNBackTrackingEnabled) {
      ProgressiveNavBackEvent({
        selectedL0: selectedL0?.title.replace(/\s/g, '') ?? '',
        selectedL1: (showL2Links && selectedL1?.n.replace(/\s/g, '')) || '',
      });
    }
    if (showL2Links) {
      setShowL2Links(false);
      const ele = document.getElementById('l1LinksContainer');
      if (ele && ele !== null) {
        ele.style.display = 'block';
      }
    } else {
      setShowL1Links(false);
      setSelectedL1Index(-1);
      const logoElement = document.getElementById('headerLogoProgressiveTaxo');
      const searchIconElement = document.getElementById('searchIconProgressiveTaxo');
      const L0LinksContainerElement = document.getElementById('navBodyContainer');
      if (logoElement) {
        logoElement.style.display = 'block';
      }
      if (
        searchIconElement &&
        (viewPortBreakpoint === 'mobile' || viewPortBreakpoint === 'tablet')
      ) {
        searchIconElement.style.display = 'block';
      }
      if (L0LinksContainerElement) {
        L0LinksContainerElement.style.display = 'flex';
      }
    }
  };

  const handleShowL2Links = () => {
    if (showL2Links) {
      setShowL2Links(false);
      setSelectedL1Index(-1);
    }
  };

  const handleClickL1LinksDisplay = (l0Data: PrimaryCategoryData) => {
    // Track the Progressive Taxo view event for mobile
    if (viewPortBreakpoint === 'mobile') {
      ProgressiveTaxoViewEvent(l0Data.title);
    }
    handleShowL2Links();
    setSelectedL0(l0Data);
    setKeyBoardNavigation(false);
    if (l0Data?.csvData && l0Data?.csvData?.length > 0) {
      if (viewPortBreakpoint !== 'desktop') {
        const ele = document.getElementById('navBodyContainer');
        if (ele && ele !== null) {
          ele.style.display = 'none';
          handleSearchDisplay();
        }
      } else {
        handleTransitions('L1');
      }
      setShowL1Links(true);
    }
  };

  const handleKeyDownL1LinksDisplay = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    l0Data: PrimaryCategoryData,
  ) => {
    handleShowL2Links();
    if (event.key === KEY_CODES.SPACE || event.key === KEY_CODES.ENTER) {
      event.preventDefault();
      setKeyBoardNavigation(true);
      setSelectedL0(l0Data);
      if (l0Data.csvData && l0Data.csvData.length > 0) {
        handleTransitions('L1');
        setShowL1Links(true);
      }
    }
  };

  const handleClickL2LinksDisplay = (l1Data: ColumnCategory, index: number) => {
    setSelectedL1(l1Data);
    setSelectedL1Index(index);
    if (viewPortBreakpoint !== 'desktop') {
      const ele = document.getElementById('l1LinksContainer');
      if (ele && ele !== null) {
        ele.style.display = 'none';
        handleSearchDisplay();
      }
    } else {
      handleTransitions('L2');
    }
    setShowL2Links(true);
  };

  const handleKeyDownL2LinksDisplay = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    l1Data: ColumnCategory,
    index: number,
  ) => {
    if (event.key === KEY_CODES.SPACE || event.key === KEY_CODES.ENTER) {
      event.preventDefault();
      setShowL2Links(true);
      setSelectedL1Index(index);
      setSelectedL1(l1Data);
      handleTransitions('L2');
    }
  };

  const handleCloseModal = () => {
    dispatch(resetSelectedCategory());
    setShowDrawer();
    document.body.style.overflow = 'scroll';
  };

  const drawerCustomProps = {
    id: 'progressiveDrawerContainer',
  };

  useEffect(() => {
    if (selectedCategoryName) {
      const selectedCategoryData = navBarData?.Taxonomy2?.find(
        (category) => category.title === selectedCategoryName,
      );
      if (selectedCategoryData) {
        setSelectedL0(selectedCategoryData);
        setShowL1Links(true);
      }
    }
  }, [selectedCategoryName, navBarData]);

  return (
    <Drawer
      {...drawerCustomProps}
      className="progressive-drawer-container"
      anchorElement={viewPortBreakpoint === 'mobile' ? null : headerRef?.current}
      attachedBackground={viewPortBreakpoint !== 'mobile'}
      open={showDrawer}
      closing={closing}
      onCloseModal={() => handleCloseModal()}
      // This prop has been removed from the type definition of the Drawer, but it still gets passed
      // to the underlying Modal component. This was removed to ensure that all Drawers would be
      // accessible by making sure they could always be closed, but this wassn't a problem for us
      // since we were already including alternate ways of closing the Drawer. The Homefield team is
      // working on a way that would allow for better ways to customize the closing button, but for
      // the time being we're just ignoring this and relying on undocumented behavior
      // ~Blair Wilcox 7/30/2025
      //
      // eslint-disable-next-line
      // @ts-ignore
      closable={false}
      direction="left">
      <div
        className="progressive-drawer-content-container hmf-display-flex hmf-flex-column"
        id="progressiveTaxo">
        <ErrorBoundaryWithLogger componentId="ProgressiveHeader-Header">
          <Header
            storeIdentifierName={storeIdentifierName}
            showL1Links={showL1Links}
            showL2Links={showL2Links}
            handleSayt={handleSayt}
            setShowDrawer={setShowDrawer}
            navigateBackToL0Menu={navigateBackToL0Menu}
          />
        </ErrorBoundaryWithLogger>
        {(showL1Links || showL2Links) && (
          <div className="hmf-display-none hmf-display-lg-block hmf-display-xlg-none">
            <Divider bottomMargin="xxxs" />
          </div>
        )}
        <div className="hmf-display-flex hmf-flex-row body-container" id="bodyContainer">
          <div
            className="nav-body-container hmf-display-flex hmf-flex-column"
            id="navBodyContainer"
            data-testid="navBodyContainer">
            <ErrorBoundaryWithLogger componentId="ProgressiveHeader-StoreLocatorAndSDD">
              <StoreLocatorAndSDD
                viewPortBreakpoint={viewPortBreakpoint}
                setShowProgressiveTaxoDrawer={setShowDrawer}
              />
            </ErrorBoundaryWithLogger>
            <ErrorBoundaryWithLogger componentId="ProgressiveHeader-L0Links">
              <L0Links
                viewPortBreakpoint={viewPortBreakpoint}
                selectedL0={selectedL0}
                taxonomy={navBarData?.Taxonomy2}
                handleClickL1LinksDisplay={handleClickL1LinksDisplay}
                handleKeyDownL1LinksDisplay={handleKeyDownL1LinksDisplay}
              />
            </ErrorBoundaryWithLogger>
            <ErrorBoundaryWithLogger componentId="ProgressiveHeader-UtilityLinks">
              <UtilityLinks navBarData={navBarData?.headerNavBar} />
            </ErrorBoundaryWithLogger>
          </div>

          {showL1Links && (
            <ErrorBoundaryWithLogger componentId="ProgressiveHeader-L1Links">
              <L1Links
                L1LinksData={selectedL0}
                viewPortBreakpoint={viewPortBreakpoint}
                keyboardNavigation={keyBoardNavigation}
                handleClickL2LinksDisplay={handleClickL2LinksDisplay}
                handleKeyDownL2LinksDisplay={handleKeyDownL2LinksDisplay}
                selectedL1Index={selectedL1Index}
                setSelectedL1Index={setSelectedL1Index}
                headerFeatured={headerFeatured}
                selectedL0={selectedL0}
                setShowL2Links={setShowL2Links}
              />
            </ErrorBoundaryWithLogger>
          )}
          {showL2Links && (
            <ErrorBoundaryWithLogger componentId="ProgressiveHeader-L2Links">
              <L2Links
                L1LinksData={selectedL0}
                L2LinksData={selectedL1}
                keyBoardNavigation={keyBoardNavigation}
              />
            </ErrorBoundaryWithLogger>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default ProgressiveHeader;
